import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from "axios";
import { HttpError } from "@/core/http/HttpError";
import { getApiBaseUrl } from "@/utils/apiUtils";
import { logger } from "@/utils/logger";

const API_BASE_URL = getApiBaseUrl();

export interface HttpClientConfig {
  timeout?: number;
  logPrefix?: string;
}

export abstract class BaseHttpClient {
  protected client: AxiosInstance;
  protected logPrefix: string;

  constructor(config: HttpClientConfig = {}) {
    const { timeout = 30000, logPrefix = "HttpClient" } = config;
    this.logPrefix = logPrefix;

    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout,
      ...(process.env.NODE_ENV === "development" && {
        withCredentials: false,
      }),
    });

    this.setupInterceptors();
  }

  protected abstract getAccessToken(): string | null;
  protected abstract handleAuthError(): void;

  protected setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => this.handleResponseError(error)
    );
  }

  protected async handleResponseError(error: AxiosError): Promise<never> {
    logger.error(`${this.logPrefix} API Error:`, {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });

    if (!error.response) {
      if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
        throw new Error("Network error: Unable to connect to the server.");
      }
      if (error.message.includes("insecure") || error.message.includes("Mixed Content")) {
        throw new Error("Security error: Mixed content blocked.");
      }
      throw new Error(`Connection error: ${error.message}`);
    }

    const { status } = error.response;

    if (error.response?.status === 401) {
      this.handleAuthError();
      throw new HttpError("Authentication required", status);
    }

    if (error.response?.status === 403) {
      throw new HttpError("Access denied.", status);
    }

    if (error.response?.status === 404) {
      const apiError = this.extractErrorMessage(error.response);
      throw new HttpError(apiError || "Resource not found.", status);
    }

    if (error.response?.status >= 500) {
      const apiError = this.extractErrorMessage(error.response);
      if (apiError) {
        logger.error(`${this.logPrefix} Server error details:`, apiError);
        throw new HttpError(`Server error: ${apiError}`, status);
      }
      throw new HttpError("Server error. Please try again later.", status);
    }

    const apiError = this.extractErrorMessage(error.response);
    if (apiError) {
      if (this.isAuthenticationError(apiError)) {
        this.handleAuthError();
        throw new HttpError("Authentication required", status);
      }
      throw new HttpError(apiError, status);
    }

    throw error;
  }

  protected extractErrorMessage(response: AxiosResponse | undefined): string | null {
    if (!response?.data || typeof response.data !== "object") {
      return null;
    }

    if ("error" in response.data && typeof response.data.error === "string") {
      return response.data.error;
    }

    if ("detail" in response.data && typeof response.data.detail === "string") {
      return response.data.detail;
    }

    return null;
  }

  protected isAuthenticationError(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes("unauthenticated") ||
      lowerMessage.includes("authentication required") ||
      lowerMessage.includes("not authenticated")
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, {
      ...config,
      data,
    });
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  isAxiosError(error: any): error is AxiosError {
    return axios.isAxiosError(error);
  }

  getBaseUrl(): string {
    return API_BASE_URL;
  }
}
